"use client";

import { Callout, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import { getMeQueryKey, Me, useUpdateMe } from "../api/endpoint.js";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { type ReactNode } from "react";
import { Form } from "radix-ui";
import { Dialog, Label, Button, TextField } from "./elements.js";
import { SaveButton } from "./save-button.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";

interface EditUserProfileDialogProps extends Dialog.RootProps {
  user: Me;
  children?: ReactNode;
}

export function EditUserProfileDialog({
  children,
  user,
  ...props
}: EditUserProfileDialogProps) {
  const [open, setOpen] = React.useState(false);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Dialog.Root
      {...props}
      open={props.open ?? open}
      onOpenChange={props.onOpenChange ?? setOpen}
    >
      <Dialog.Trigger>{children}</Dialog.Trigger>

      <Dialog.Content maxWidth="480px">
        <Content user={user} onClose={handleClose} />
      </Dialog.Content>
    </Dialog.Root>
  );
}

interface ContentProps extends Pick<EditUserProfileDialogProps, "user"> {
  onClose: () => void;
}

function Content({ user, onClose }: ContentProps) {
  const translate = useTranslation();
  const client = useQueryClient();
  const updateMe = useUpdateMe({
    mutation: {
      onSettled: () => {
        client.invalidateQueries({ queryKey: getMeQueryKey() });
      },
      onSuccess: (newProfile) => {
        client.setQueryData(getMeQueryKey(), {
          ...user,
          firstName: newProfile.firstName || user.firstName,
          lastName: newProfile.lastName || user.lastName,
        });
      },
    },
  });

  return (
    <>
      <Dialog.Title mb="5">
        <Translation
          defaultMessage="Edit name"
          id="pcK9ly"
          description="Dialog title for editing user name"
        />
      </Dialog.Title>
      <VisuallyHidden>
        <Dialog.Description>
          <Translation
            defaultMessage="Edit the details of {email}"
            id="SEsXx0"
            description="Dialog description for editing user details"
            values={{ email: <Text weight="bold">{user.email}</Text> }}
          />
        </Dialog.Description>
      </VisuallyHidden>

      {updateMe.error ? (
        <Callout.Root color="red" my="-2">
          <Callout.Text>{getMutationErrorMessage(updateMe.error)}</Callout.Text>
        </Callout.Root>
      ) : null}

      <Form.Root
        onSubmit={async (event) => {
          event.preventDefault();
          const formData = new FormData(event.currentTarget);
          const firstName = formData.get("firstName")?.toString();
          const lastName = formData.get("lastName")?.toString();
          updateMe.mutate({
            data: {
              firstName: firstName ?? undefined,
              lastName: lastName ?? undefined,
            },
          });
        }}
      >
        <Flex my="5" direction="column" gap="4">
          <Form.Field name="firstName" asChild>
            <Flex direction="column" gap="1">
              <Form.Label asChild>
                <Label>
                  <Translation
                    defaultMessage="First name"
                    id="DAYdcg"
                    description="Label for first name field"
                  />
                </Label>
              </Form.Label>
              <Form.Control asChild>
                <TextField
                  data-1p-ignore
                  autoComplete="given-name"
                  defaultValue={user.firstName ?? ""}
                  placeholder={translate({
                    defaultMessage: "Your first name",
                    id: "gUTpgG",
                    description: "Placeholder for first name field",
                  })}
                  disabled={updateMe.isPending || updateMe.isSuccess}
                />
              </Form.Control>
            </Flex>
          </Form.Field>

          <Form.Field name="lastName" asChild>
            <Flex direction="column" gap="1">
              <Form.Label asChild>
                <Label>
                  <Translation
                    defaultMessage="Last name"
                    id="IwcAMv"
                    description="Label for last name field"
                  />
                </Label>
              </Form.Label>
              <Form.Control asChild>
                <TextField
                  data-1p-ignore
                  autoComplete="family-name"
                  defaultValue={user.lastName ?? ""}
                  placeholder={translate({
                    defaultMessage: "Your last name",
                    id: "Gy8eW0",
                    description: "Placeholder for last name field",
                  })}
                  disabled={updateMe.isPending || updateMe.isSuccess}
                />
              </Form.Control>
            </Flex>
          </Form.Field>
        </Flex>

        <Flex mt="5" gap="3" justify="end">
          <Dialog.Close>
            <Button
              variant="secondary"
              disabled={updateMe.isPending || updateMe.isSuccess}
            >
              <Translation
                defaultMessage="Cancel"
                id="hHNj31"
                description="Cancel button text"
              />
            </Button>
          </Dialog.Close>
          <SaveButton
            type="submit"
            loading={updateMe.isPending}
            done={updateMe.isSuccess}
            onDone={onClose}
          >
            <Translation
              defaultMessage="Save"
              id="RT8KNi"
              description="Save button text"
            />
          </SaveButton>
        </Flex>

        {/* mirror errors in a live region */}
        <VisuallyHidden asChild>
          <section aria-live="polite">
            {getMutationErrorMessage(updateMe.error)}
          </section>
        </VisuallyHidden>
      </Form.Root>
    </>
  );
}

function getMutationErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }

  // TODO Handle server errors
  return "Something went wrong. Please try again.";
}

// Note: Error messages in getMutationErrorMessage are kept as plain strings
// since they are displayed dynamically
